export const partners = {
  meta: {
    title: 'Become a BWC Partner: Better Website Co.',
    description:
      'Refer businesses that need a better website and earn commission when they become a client. Three tiers, 20% to 50%. Commission-only — no guaranteed income.',
  },
  hero: {
    eyebrow: 'Better Website Co. Partners',
    headline: 'Refer Businesses.',
    accentWord: 'Earn Commission.',
    subhead:
      "If you have access to business owners, or you're just good at spotting people who need a better website, introduce them to Better Website Co. We handle the sales, design and development. When they become a paying client, you earn a commission — 20% to 50%, depending on your tier.",
  },
  explain: {
    eyebrow: 'Who this is for',
    headline: "People with access to business owners, and people who are good at finding potential clients.",
    types: [
      'Brand designers & photographers',
      'Social media managers',
      'Event planners',
      'Copywriters & marketing consultants',
      'Sales & business development people',
      'Community & network builders',
      'Freelancers who work alongside business owners',
      'Anyone whose network includes people who need a website',
    ],
  },
  howItWorks: {
    eyebrow: 'How it works',
    headline: 'Five steps, start to finish.',
    steps: [
      { title: 'Find a business', copy: 'Spot a business that needs a better website — through your work, your network, or just paying attention.' },
      { title: 'Introduce them to BWC', copy: "Send us who they are and what they need. Once you're accepted, /partner/onboarding walks you through exactly how to do this." },
      { title: 'We handle the rest', copy: "We run the strategy call and manage the sales, design and development. You don't have to do anything else." },
      { title: 'The client pays', copy: 'Once they sign on and pay for the project, the referral is confirmed.' },
      { title: 'You receive your commission', copy: "Your commission goes out within 7 days of the client's payment." },
    ],
  },
  status: {
    eyebrow: 'Staying in the loop',
    headline: "You'll always know where things stand.",
    steps: [
      { emoji: '✅', label: 'Referral received' },
      { emoji: '👀', label: "We've contacted your referral" },
      { emoji: '🎉', label: 'They became a client' },
      { emoji: '💸', label: 'Your commission is sent' },
    ],
    note: "We won't share what the client paid. Just where things stand with your referral.",
  },
  rewards: {
    eyebrow: 'Commission',
    headline: 'Three tiers. Commission grows with what you actually bring.',
    subhead:
      "Every partner starts at Level 1. You move up by doing more than posting content — each tier is earned through what you actually contribute, not just effort.",
    disclaimer:
      "This is commission-only. There's no base pay, no guaranteed income, and no commission owed unless a business you refer actually becomes a paying client.",
    tiers: [
      {
        level: 'Level 1',
        name: 'Referral Partner',
        percent: '20%',
        description:
          'Find businesses and introduce them to BWC through your network, DMs, email, or any normal outreach method. BWC handles the sales and the website — no content requirement.',
      },
      {
        level: 'Level 2',
        name: 'BWC Content Partner',
        percent: '35%',
        description:
          "Use BWC's content strategy to create content designed to attract businesses that need a website. Keep BWC attribution in your bio while actively participating, and follow our content guidelines.",
      },
      {
        level: 'Level 3',
        name: 'BWC Growth Partner',
        percent: 'Up to 50%',
        description:
          "Reserved for partners who consistently generate qualified opportunities for BWC, not just post content. Earned through demonstrated results, on a per-project basis.",
      },
    ],
    guidelines: {
      headline: 'Content guidelines for Level 2 and 3',
      items: [
        "Don't spam businesses",
        "Don't create misleading content",
        "Don't post low-effort or intentionally low-quality content just to meet a requirement",
        "Don't make promises about pricing, results or services that BWC hasn't approved",
        "Don't misrepresent yourself as a BWC employee",
        'Content should be useful, relevant and consistent with the BWC brand',
      ],
    },
    choice: {
      headline: 'Choose your reward.',
      options: [
        { emoji: '💰', label: 'Cash' },
        { emoji: '🎁', label: 'Credit toward your own website or future services' },
      ],
    },
  },
  strategic: {
    eyebrow: 'Strategic Partners',
    headline: 'Meet business owners as part of your work?',
    copy:
      "If you're a brand designer, photographer, social media manager, event planner, copywriter, or marketing consultant, you're probably introducing businesses to people like us all the time. We'd like to formalize that: higher commission, priority, and a longer-term relationship. Reach out and let's talk.",
    cta: { label: 'Reach out about Strategic Partnership' },
  },
  application: {
    eyebrow: 'Ready to become a BWC Partner?',
    headline: 'Tell us a little about yourself and how you plan to find businesses.',
    formName: 'partner-application',
    submitLabel: 'Apply to Become a BWC Partner',
    nextSteps:
      "We'll review your application and contact you on the WhatsApp number or email you provided if you're selected to become a BWC Partner. If selected, we'll walk you through everything you need to know before you start referring businesses.",
  },
  faq: {
    eyebrow: 'FAQ',
    headline: 'Questions, answered.',
    items: [
      { q: 'Who can apply?', a: 'Anyone with access to business owners, or a good eye for spotting businesses that need a better website: friends, family, past clients, followers, or people you work alongside professionally.' },
      { q: 'Is this commission-only?', a: "Yes. There's no base pay or guaranteed income. You earn a commission only when a business you refer actually becomes a paying client." },
      { q: 'How much do I earn?', a: '20% as a Referral Partner, 35% as a BWC Content Partner, and up to 50% as a BWC Growth Partner. You start at Level 1 and move up by doing more than posting content — each tier is earned, not automatic.' },
      { q: 'Do I have to post content to earn more?', a: "Posting content alone doesn't move you up a tier. Level 2 requires actively using BWC's content strategy with quality standards; Level 3 is earned through demonstrated results — consistently generating qualified opportunities, not simply posting." },
      { q: 'When do I get paid?', a: "After the client signs on and pays in full (or reaches an agreed payment milestone). Your commission goes out within 7 days of that." },
      { q: "How do I refer someone once I'm a partner?", a: "Once accepted, you'll get access to /partner/onboarding (how to pitch, what not to promise) and /partner/refer (a direct referral submission form) instead of relying on WhatsApp for every referral." },
      { q: 'Is there a limit?', a: "No. Refer as many businesses as you'd like; each successful referral is rewarded on its own." },
      { q: 'Do I need experience?', a: 'No. You just need to know a business that could use a better website, or be good at finding one.' },
      { q: 'Can businesses refer businesses?', a: 'Yes. If a past or current client sends us another business, that counts too.' },
    ],
  },
  finalCta: {
    headline: 'Know someone who needs a website?',
    subhead: "Apply to become a partner, and let's reward you for the introduction.",
    cta: { label: 'Become a BWC Partner' },
  },
}
